import type {
  ClientMessage,
  CyclesClientMessage,
  IceClientMessage,
  ServerMessage,
} from '@xox/shared'

export type ConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'closed'

interface Handlers {
  onMessage: (msg: ServerMessage) => void
  onStatus: (status: ConnectionStatus) => void
}

const PING_MS = 10_000
const BACKOFF_MIN = 500
const BACKOFF_MAX = 8000

/** Reconnecting WebSocket for one room. Never resends moves: the server snapshot is the truth. */
export class RoomSocket {
  private ws: WebSocket | null = null
  private attempts = 0
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private stopped = false
  private readonly code: string
  private readonly handlers: Handlers
  private readonly path: string

  constructor(code: string, handlers: Handlers, path = '/ws') {
    this.code = code
    this.handlers = handlers
    this.path = path
    document.addEventListener('visibilitychange', this.onVisibility)
    window.addEventListener('online', this.onOnline)
  }

  connect() {
    if (this.stopped) return
    this.clearTimers()
    this.handlers.onStatus(this.attempts === 0 ? 'connecting' : 'reconnecting')
    const proto = location.protocol === 'https:' ? 'wss' : 'ws'
    const ws = new WebSocket(
      `${proto}://${location.host}${this.path}?room=${encodeURIComponent(this.code)}`,
    )
    this.ws = ws

    ws.onopen = () => {
      this.attempts = 0
      this.handlers.onStatus('open')
      this.sendPing()
      this.pingTimer = setInterval(() => this.sendPing(), PING_MS)
    }
    ws.onmessage = (ev) => {
      try {
        this.handlers.onMessage(JSON.parse(ev.data as string) as ServerMessage)
      } catch {
        // ignore malformed frames
      }
    }
    ws.onclose = (ev) => {
      this.clearTimers()
      this.ws = null
      if (this.stopped) return
      // 44xx are our own "don't retry" codes (no identity / room gone).
      if (ev.code >= 4400 && ev.code < 4500) {
        this.handlers.onStatus('closed')
        return
      }
      this.scheduleReconnect()
    }
    ws.onerror = () => {
      // onclose follows; nothing to do here
    }
  }

  send(msg: ClientMessage | IceClientMessage | CyclesClientMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg))
  }

  close() {
    this.stopped = true
    this.clearTimers()
    document.removeEventListener('visibilitychange', this.onVisibility)
    window.removeEventListener('online', this.onOnline)
    this.ws?.close(1000, 'leaving')
    this.ws = null
    this.handlers.onStatus('closed')
  }

  private sendPing() {
    this.send({ type: 'ping', t: Date.now() })
  }

  private scheduleReconnect() {
    const base = Math.min(BACKOFF_MAX, BACKOFF_MIN * 2 ** this.attempts)
    const delay = base / 2 + Math.random() * (base / 2)
    this.attempts++
    this.handlers.onStatus('reconnecting')
    this.reconnectTimer = setTimeout(() => this.connect(), delay)
  }

  private clearTimers() {
    if (this.pingTimer) clearInterval(this.pingTimer)
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this.pingTimer = null
    this.reconnectTimer = null
  }

  private onVisibility = () => {
    if (document.visibilityState !== 'visible' || this.stopped) return
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      this.attempts = 0
      this.connect()
    } else {
      this.sendPing()
    }
  }

  private onOnline = () => {
    if (!this.stopped && (!this.ws || this.ws.readyState !== WebSocket.OPEN)) {
      this.attempts = 0
      this.connect()
    }
  }
}
